import React, { useMemo, useState } from 'react';
import { useAppointmentRequestModalContext } from '../../Web/AppointmentRequests/utils';
import { CreateMessageModal } from '../ContentModals/CreateMessageModal';
import { useDispatch } from 'react-redux';
import {
  CREATE_APPOINTMENT_REQUEST_MESSAGE_ERROR,
  createAppointmentRequestMessage,
  UPLOAD_APPOINTMENT_REQUEST_ATTACHMENT,
  uploadAppointmentRequestAttachment,
} from '../../../redux/actions/appointmentRequestActions';
import { useQueryClient } from 'react-query';
import { cacheKeys } from '../../../hooks/appointment-requests/cacheKeys';
import ResultModal from '../../../shared/components/Modals/MessageModal';

export function AppointmentRequestMessageModal() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {
    isMessageModalOpen,
    setIsMessageModalOpen,
    appointmentRequestData,
    handleRefetchAppointmentRequests,
  } = useAppointmentRequestModalContext();

  const [isLoading, setIsLoading] = useState(false);
  const [responseState, setResponseState] = useState({
    show: false,
    type: '',
    message: '',
  });

  const contextSubheading = useMemo(() => {
    const petName = appointmentRequestData?.petName;
    const typeTitle = appointmentRequestData?.typeTitle;

    if (!petName || !typeTitle) {
      return '';
    }

    return `Re: Appointment Request for ${petName} (${typeTitle})`;
  }, [appointmentRequestData]);

  const handleSendMessage = async ({
    message,
    requiresResponse,
    attachments,
    onSuccess,
  }) => {
    if (
      !message &&
      attachments.images.length === 0 &&
      attachments.pdfs.length === 0
    ) {
      return;
    }

    setIsLoading(true);

    try {
      const uploadedAttachments = { images: [], pdfs: [] };

      for (const image of attachments.images) {
        const result = await dispatch(
          uploadAppointmentRequestAttachment(image, 'images'),
        );
        if (result.type === UPLOAD_APPOINTMENT_REQUEST_ATTACHMENT) {
          uploadedAttachments.images.push(result.payload.secure_url);
        }
      }

      for (const pdf of attachments.pdfs) {
        const result = await dispatch(
          uploadAppointmentRequestAttachment(pdf, 'pdfs'),
        );
        if (result.type === UPLOAD_APPOINTMENT_REQUEST_ATTACHMENT) {
          uploadedAttachments.pdfs.push(result.payload.secure_url);
        }
      }

      const messageResult = await dispatch(
        createAppointmentRequestMessage({
          appointmentRequestId: appointmentRequestData.id,
          message,
          requiresResponse,
          attachments: uploadedAttachments,
        }),
      );

      if (messageResult.type === CREATE_APPOINTMENT_REQUEST_MESSAGE_ERROR) {
        throw new Error('Failed to send message');
      }

      setResponseState({
        show: true,
        type: 'success',
        message: 'Message sent successfully!',
      });

      setIsMessageModalOpen(false);
      onSuccess?.();
      if (handleRefetchAppointmentRequests) {
        handleRefetchAppointmentRequests(false);
      }

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });

        queryClient.invalidateQueries(
          cacheKeys.getAppointmentRequestMessages({
            appointmentRequestId: appointmentRequestData.id,
          }),
        );
      }, 2000);
    } catch (error) {
      console.error(error);

      setResponseState({
        show: true,
        type: 'danger',
        message: 'Failed to upload attachments or send message.',
      });

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CreateMessageModal
        isOpen={isMessageModalOpen}
        setIsOpen={setIsMessageModalOpen}
        contextSubheading={contextSubheading}
        onSave={handleSendMessage}
        isLoading={isLoading}
      />

      <ResultModal
        show={responseState.show}
        type="alert"
        alertType={responseState.type}
        message={responseState.message}
        onClose={() => setResponseState({ show: false, type: '', message: '' })}
      />
    </>
  );
}
